import {
	Button,
	Checkbox,
	Divider,
	Drawer,
	Group,
	Loader,
	Radio,
	Select,
	Stack,
	Text,
} from '@mantine/core';
import { MultiSelect } from 'react-hook-form-mantine';
import { DocumentTemplateStatus, TemplateAvailability } from '@dealstack/shared/lib/generated';
import { useDealOptionsQuery } from '@dealstack/frontend/app/pages/DocumentTemplates/api/useDealOptionsQuery';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useTemplateAvailabilityForm } from './useTemplateAvailabilityForm';
import {
	KYC_AUDIENCE_OPTIONS,
	KYC_CATEGORY_NONE,
	KYC_CATEGORY_OPTIONS,
	STATUS_OPTIONS,
} from './useTemplateAvailabilityForm.types';
import { FormStack } from './TemplateAvailabilityDrawer.styled';

interface Props {
	templateId: string;
	opened: boolean;
	onClose: () => void;
}

export const TemplateAvailabilityDrawer = ({ templateId, opened, onClose }: Props) => {
	const {
		control,
		handleSubmit,
		status,
		setStatus,
		availability,
		setAvailability,
		kycAudience,
		setKycAudience,
		kycCategory,
		setKycCategory,
		isLoading,
		isSaving,
	} = useTemplateAvailabilityForm({ templateId, onSaved: onClose });
	const { data: dealOptions, isLoading: dealsLoading } = useDealOptionsQuery();
	const tDefault = useTranslationDefault();
	const statusOptions = STATUS_OPTIONS.map(({ value, labelKey }) => ({
		value,
		label: tDefault(labelKey),
	}));
	const categoryOptions = KYC_CATEGORY_OPTIONS.map(({ value, labelKey }) => ({
		value,
		label: tDefault(labelKey),
	}));

	const isGlobal = availability === TemplateAvailability.ALL_DEALS;
	const isDraft = status === DocumentTemplateStatus.DRAFT;
	const isKycForm = kycAudience.length > 0;

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			title={tDefault('document_templates.assign.title')}
			position="right"
		>
			{isLoading || dealsLoading ? (
				<Loader />
			) : (
				<form onSubmit={handleSubmit}>
					<FormStack>
						<Text size="sm">
							{tDefault('document_templates.assign.published_note')}
						</Text>
						<Select
							data-testid="template-status-select"
							label={tDefault('document_templates.assign.status_label')}
							value={status}
							onChange={(value) => {
								// Narrowed by lookup rather than cast, so an unexpected value is
								// ignored instead of asserted onto the enum.
								const next = STATUS_OPTIONS.find(
									(option) => option.value === value
								)?.value;
								if (next !== undefined) {
									setStatus(next);
								}
							}}
							data={statusOptions}
							allowDeselect={false}
							disabled={isSaving}
						/>
						<Radio.Group
							value={availability}
							onChange={(value) => {
								// Mantine types onChange as (value: string), so the value is
								// narrowed by comparison. Never with a cast.
								if (
									value === TemplateAvailability.ALL_DEALS ||
									value === TemplateAvailability.SELECTED_DEALS
								) {
									setAvailability(value);
								}
							}}
							label={tDefault('document_templates.assign.availability_label')}
						>
							{/* Radio.Group lays children out with no gap of its own. */}
							<Stack gap="xs" mt="xs">
								<Radio
									value={TemplateAvailability.SELECTED_DEALS}
									label={tDefault(
										'document_templates.assign.availability_selected'
									)}
								/>
								<Radio
									value={TemplateAvailability.ALL_DEALS}
									label={tDefault('document_templates.assign.availability_all')}
								/>
							</Stack>
						</Radio.Group>
						<MultiSelect
							name="dealIds"
							control={control}
							label={tDefault('document_templates.assign.deals_label')}
							searchable
							disabled={isGlobal}
							data={dealOptions ?? []}
							data-testid="deal-picker"
						/>
						{/* A deal picker is meaningless while the template cannot reach any deal,
						    so say so rather than leaving the Save button looking effective. */}
						{isDraft && (
							<Text size="xs" c="dark.0" data-testid="template-draft-hint">
								{tDefault('document_templates.assign.draft_hint')}
							</Text>
						)}
						<Divider />
						{/* The tag is what puts this template in the KYC picker; without an
						    audience it stays a plain document. */}
						<Checkbox.Group
							value={kycAudience}
							onChange={(values) =>
								setKycAudience(
									// Narrowed by lookup, so Mantine's string[] never lands on
									// the enum by assertion.
									KYC_AUDIENCE_OPTIONS.filter((option) =>
										values.includes(option.value)
									).map((option) => option.value)
								)
							}
							label={tDefault('document_templates.assign.kyc.audience_label')}
							description={tDefault('document_templates.assign.kyc.audience_hint')}
						>
							<Stack gap="xs" mt="xs">
								{KYC_AUDIENCE_OPTIONS.map(({ value, labelKey }) => (
									<Checkbox
										key={value}
										value={value}
										label={tDefault(labelKey)}
										disabled={isSaving}
										data-testid={`kyc-audience-${value}`}
									/>
								))}
							</Stack>
						</Checkbox.Group>
						<Select
							data-testid="kyc-category-select"
							label={tDefault('document_templates.assign.kyc.category_label')}
							value={kycCategory ?? KYC_CATEGORY_NONE}
							onChange={(value) => {
								const next = KYC_CATEGORY_OPTIONS.find(
									(option) => option.value === value
								)?.value;
								if (next !== undefined) {
									setKycCategory(next === KYC_CATEGORY_NONE ? null : next);
								}
							}}
							data={categoryOptions}
							allowDeselect={false}
							// A category is only reachable through an audience, and the server
							// drops it when the audience empties.
							disabled={isSaving || !isKycForm}
						/>
						<Group justify="flex-end">
							<Button variant="subtle" onClick={onClose} type="button">
								{tDefault('document_templates.assign.cancel')}
							</Button>
							<Button type="submit" loading={isSaving}>
								{tDefault('document_templates.assign.save')}
							</Button>
						</Group>
					</FormStack>
				</form>
			)}
		</Drawer>
	);
};
